import { useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { requestPOST } from 'src/utils/baseAPI';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { createTargetActivities, classifyActivity, formatTooltip, debugUnclassifiedActivities } from 'src/utils/chartUtils';

const RenderChartActionLog = ({ fromDateToDate, selectedBranch }) => {
  const [chartOptions, setChartOptions] = useState({});
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState({});
  const intl = useIntl();

  // Chọn các hoạt động quan trọng cần thống kê (có thể thay đổi theo nhu cầu)
  const targetActivities = createTargetActivities([ 7, 8, 9, 11, '12_RECORD', '12_TICKET', 13]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Lấy dữ liệu nhật ký hoạt động
        const res = await requestPOST('api/v1/actionlogitems/search', {
          dateFrom: fromDateToDate?.fromDate.clone().add(7, 'hours'),
          dateStop: fromDateToDate?.toDate.clone().add(7, 'hours'),
          branchId: selectedBranch ?? null,
          pageNumber: 1,
          pageSize: 10000, // Lấy nhiều dữ liệu để tính toán
        });

        const data = res?.data ?? [];
        setTotalCount(res?.totalCount ?? 0);
        
        // Debug: Log một vài record đầu để kiểm tra cấu trúc dữ liệu
        console.log('📊 Action Log Sample Data:', data.slice(0, 3));
        console.log('📊 Available activity types:', targetActivities.map(a => `${a.id}: ${a.name}`));
        
        // Debug: Kiểm tra các activity không được phân loại
        debugUnclassifiedActivities(data, targetActivities);
        
        // Filter và phân loại dữ liệu theo các hoạt động cần thống kê
        const groupedByDate = {};
        const activityCount = {};
        
        // Khởi tạo activityCount
        targetActivities.forEach(activity => {
          activityCount[activity.key] = 0;
        });

        data.forEach(record => {
          const eventDate = record.timeStamp ? moment(record.timeStamp).format('DD/MM/YYYY') : null;
          const note = record.note || '';
          const detail = record.detail || '';
          const type = record.type;
          
          if (eventDate) {
            if (!groupedByDate[eventDate]) {
              groupedByDate[eventDate] = {};
              targetActivities.forEach(activity => {
                groupedByDate[eventDate][activity.key] = 0;
              });
            }
            
            // Phân loại dựa trên type trước, sau đó mới dựa trên note và detail
            let activityType = classifyActivity(record, targetActivities);
            
            if (activityType) {
              groupedByDate[eventDate][activityType]++;
              activityCount[activityType]++;
            }
          }
        });

        setSummary(activityCount);

        // Chuyển đổi thành dữ liệu cho biểu đồ
        const dates = Object.keys(groupedByDate).sort((a, b) => 
          moment(a, 'DD/MM/YYYY').diff(moment(b, 'DD/MM/YYYY'))
        );

        const series = targetActivities.map(activity => {
          return {
            name: activity.name,
            data: dates.map(date => groupedByDate[date][activity.key] || 0),
            type: 'column',
            color: activity.color
          };
        });

        // Thêm line chart cho tổng hoạt động
        const totalData = dates.map(date => {
          return targetActivities.reduce((sum, activity) => {
            return sum + (groupedByDate[date][activity.key] || 0);
          }, 0);
        });

        series.push({
          name: 'Tổng hoạt động quan trọng',
          data: totalData,
          type: 'spline',
          yAxis: 1,
          color: '#2C3E50',
          lineWidth: 4,
          marker: {
            enabled: true,
            radius: 5,
            fillColor: '#FFFFFF',
            lineColor: '#2C3E50',
            lineWidth: 2
          }
        });

        const options = {
          chart: {
            height: 500,
            zoomType: 'xy'
          },
          title: {
            text: 'Thống kê hoạt động quan trọng của hệ thống',
            style: {
              fontSize: '18px',
              fontWeight: 'bold'
            }
          },
          subtitle: {
            text: `Từ ${fromDateToDate?.fromDate?.format('DD/MM/YYYY')} đến ${fromDateToDate?.toDate?.format('DD/MM/YYYY')}`
          },
          xAxis: {
            categories: dates,
            title: {
              text: 'Ngày'
            },
            crosshair: true
          },
          yAxis: [
            {
              title: {
                text: 'Số lượng hoạt động theo loại',
                style: {
                  color: Highcharts.getOptions().colors[0]
                }
              },
              min: 0
            },
            {
              title: {
                text: 'Tổng hoạt động',
                style: {
                  color: '#2C3E50'
                }
              },
              opposite: true,
              min: 0
            }
          ],
          tooltip: {
            shared: true,
            formatter: formatTooltip
          },
          legend: {
            align: 'center',
            verticalAlign: 'bottom',
            layout: 'horizontal'
          },
          plotOptions: {
            column: {
              pointPadding: 0.2,
              borderWidth: 0,
              dataLabels: {
                enabled: false
              }
            },
            line: {
              dataLabels: {
                enabled: true,
                format: '{y}'
              }
            },
            spline: {
              dataLabels: {
                enabled: true,
                format: '{y}',
                style: {
                  fontWeight: 'bold',
                  color: '#2C3E50'
                }
              },
              marker: {
                lineWidth: 2,
                fillColor: '#FFFFFF'
              }
            }
          },
          series: series,
          credits: {
            enabled: false
          }
        };

        setChartOptions(options);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching action log data:', error);
        setLoading(false);
      }
    };

    if (fromDateToDate?.fromDate && fromDateToDate?.toDate) {
      fetchData();
    }
  }, [fromDateToDate, selectedBranch]);

  return (
    <div className="card card-xl-stretch mb-xl-5">
      <div className="card-header min-h-50px">
        <h3 className="card-title fw-bold text-primary">Thống kê hoạt động</h3>
        <div className="card-toolbar">
          <span className="badge badge-light-primary fs-7 me-2">
            Tổng: {Object.values(summary).reduce((sum, count) => sum + count, 0)} hoạt động
          </span>
        </div>
      </div>
      <div className="card-body">
        {/* Summary cards */}
        <div className="row mb-4">
          {targetActivities.map((activity) => {
            const count = summary[activity.key] || 0;
            
            return (
              <div key={activity.key} className="col-xl-3 col-lg-4 col-md-6 col-sm-6 col-12 mb-3">
                <div className="card card-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="symbol symbol-40px me-3">
                        <div className="symbol-label" style={{backgroundColor: activity.color + '20'}}>
                          <i className="fas fa-chart-bar fs-5" style={{color: activity.color}}></i>
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <div className="text-gray-900 fw-bold fs-6">{count}</div>
                        <div className="text-muted fw-semibold fs-8" title={activity.name}>
                          {activity.name.length > 15 ? 
                            activity.name.substring(0, 15) + '...' : 
                            activity.name
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {loading ? (
          <div className="d-flex justify-content-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
          />
        )}
      </div>
    </div>
  );
};

export default RenderChartActionLog; 